import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ClientCertificate, ENVIRONMENT, Environment } from '@netfoundry-ui/shared/model';
import { result, toLower } from 'lodash';
import { catchError, delay } from 'rxjs/operators';
import { throwError as observableThrowError } from 'rxjs/internal/observable/throwError';
import {
    GetOption,
    HateoasResourceOperation,
    HttpMethod,
    PagedGetOption,
    PagedResourceCollection,
} from '@lagoshny/ngx-hateoas-client';
import { map, take, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { HTTP_CLIENT, TokenService } from './token.service';
import { LoggerService } from './logger.service';


/** A page of client certificates read from the v3 API: the rows, and the total the service gave. */
export interface ClientCertificatePageV3 {
    certificates: ClientCertificate[];
    total: number | null;
}

/**
 * A new client certificate.
 *
 * `name` is this certificate's, and is unique within the account across both certificates and the
 * tokens that produce them. A certificate enrolled against a token takes its name from that token
 * instead, so this path is for the administrator writing one directly.
 */
export interface CreateClientCertificateV3 {
    name: string;
    value: string;
    type: 'CERTIFICATE' | 'CSR';
    validYears?: number;
    contactEmail?: string;
}

/**
 * A replacement body for a certificate already on file.
 *
 * `CERTIFICATE` stores what is given as it is. `CSR` has NetFoundry sign the request with the
 * account's intermediate CA, and only then is `validYears` read.
 *
 * No `name`: leaving it off is what makes the write a replacement rather than a rename.
 *
 * `retainPrevious` defaults to true on the service: the previous certificate stays in the stored
 * PEM until it expires, so holders who have not moved yet keep working. False drops it, which is
 * what a compromised key needs.
 */
export interface RenewClientCertificateV3 {
    value: string;
    type: 'CERTIFICATE' | 'CSR';
    validYears?: number;
    retainPrevious?: boolean;
}

/** How close a certificate is to the end of its life. `unknown` is a certificate with no expiry. */
export type CertificateExpiryStatus = 'expired' | 'expiring' | 'valid' | 'unknown';

export interface CertificateExpiryBadge {
    tone: 'ok' | 'warn' | 'err' | 'muted';
    label: string;
}

/**
 * When a certificate starts reading as expiring. Sixty days, because that is the first rung of the
 * notice ladder the frontdoor service runs — it writes to the contact at 60, 30, 14 and 7 days out.
 * A row drawn amber here is therefore one somebody has already been told about.
 */
export const CERTIFICATE_EXPIRY_WARNING_DAYS = 60;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Alerting's MAJOR rung for an expiring certificate. Its ladder is expired CRITICAL, 7 days MAJOR, 30 days MINOR and
 * 60 days INFO, and each rung includes its bound.
 */
export const CERTIFICATE_EXPIRY_URGENT_DAYS = 7;

/** A certificate's band on alerting's ladder: `urgent` is MAJOR, `expiring` is MINOR or INFO, `valid` raises no alert. */
export type CertificateExpiryUrgency = 'expired' | 'urgent' | 'expiring' | 'valid' | 'unknown';

/** The band for `expiresAt`: {@link certificateExpiryStatus}, with its last seven days split out as `urgent`. */
export function certificateExpiryUrgency(expiresAt: string | undefined, now = Date.now()): CertificateExpiryUrgency {
    const status = certificateExpiryStatus(expiresAt, now);
    if (status !== 'expiring') {
        return status;
    }
    return (expiryTime(expiresAt) as number) <= now + CERTIFICATE_EXPIRY_URGENT_DAYS * MS_PER_DAY ? 'urgent' : 'expiring';
}

/** The expiry as epoch milliseconds, or null where there is no usable date. */
function expiryTime(expiresAt: string | undefined): number | null {
    const expiry = expiresAt ? new Date(expiresAt).getTime() : NaN;
    return Number.isNaN(expiry) ? null : expiry;
}

/**
 * Whole days until `expiresAt`, or null where there is no usable date. Rounded down, so a
 * certificate with a few hours left reads as 0 rather than as a day it does not have.
 */
export function certificateDaysRemaining(expiresAt: string | undefined, now = Date.now()): number | null {
    if (!expiresAt) {
        return null;
    }
    const expiry = new Date(expiresAt).getTime();
    if (Number.isNaN(expiry)) {
        return null;
    }
    return Math.floor((expiry - now) / MS_PER_DAY);
}

/**
 * How to read a certificate's expiry. It compares the exact instant and each bound is inclusive, as alerting's ladder
 * is, so a listing and an alert about the same certificate agree. An absent date is `unknown`, not `valid`.
 */
export function certificateExpiryStatus(expiresAt: string | undefined, now = Date.now()): CertificateExpiryStatus {
    const expiry = expiryTime(expiresAt);
    if (expiry === null) {
        return 'unknown';
    }
    if (expiry <= now) {
        return 'expired';
    }
    return expiry <= now + CERTIFICATE_EXPIRY_WARNING_DAYS * MS_PER_DAY ? 'expiring' : 'valid';
}

/**
 * How to draw that. One place, so a listing and anything else reading the same certificate cannot
 * disagree. An expiring certificate counts itself down, because the row beside it gives the date
 * and the question the reader has is how long is left.
 */
export function certificateExpiryBadge(expiresAt: string | undefined, now = Date.now()): CertificateExpiryBadge {
    const status = certificateExpiryStatus(expiresAt, now);
    if (status === 'unknown') {
        return { tone: 'muted', label: 'Unknown' };
    }
    if (status === 'expired') {
        return { tone: 'err', label: 'Expired' };
    }
    if (status === 'valid') {
        return { tone: 'ok', label: 'Valid' };
    }
    const days = certificateDaysRemaining(expiresAt, now) as number;
    if (days === 0) {
        return { tone: 'warn', label: 'Expires today' };
    }
    return { tone: 'warn', label: `${days} ${days === 1 ? 'day' : 'days'} left` };
}

@Injectable({ providedIn: 'root' })
export class ClientCertificateService extends HateoasResourceOperation<ClientCertificate> {
    apiUrl;
    appsUrl;
    networkPricing = 199.0;
    regionPricing = [{ region: null, price: 0.2, rate: 'GB' }];
    lastPageCount = 0;
    lastTotalCount = 0;
    model: ClientCertificate | null = null;
    static defaultHttpAccept = {
        headers: { accept: 'application/hal+json' },
        pageParams: {
            page: 0,
            size: 2000,
        },
    };

    constructor(
        private logger: LoggerService,
        private tokenService: TokenService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        super(ClientCertificate);
        this.apiUrl = this.environment.frontdoorURL;

    }


    public findClientCertificates(id: string, isV2:boolean, options?: PagedGetOption): Promise<ClientCertificate[]> {
          const headers = new HttpHeaders().set('Accept', 'application/hal+json');
          const version = isV2 ? '/v2' : '';
        
          let params = new HttpParams().set('sort', 'createdAt,DESC');
        if(options) {
    if (options.pageParams) {
      Object.keys(options.pageParams).forEach(key => {
        const value = options.pageParams[key]; // <-- Corrected
        if (value !== undefined && value !== null) {
          params = params.append(key, String(value));
        }
      });
    }

   // Handle the nested params object
    if (options.params) {
      Object.keys(options.params).forEach(key => {
        const value = options.params[key]; // <-- Corrected
        if (value !== undefined && value !== null) {
          params = params.append(key, String(value));
        }
      });
    
  }
}
        return this.http
                .get<any>(`${this.apiUrl}${version}/${id}/client-certificates`, {
                    headers,
                    params,
                    })
            .pipe(
                tap((val) => {
                    this.lastPageCount = val.page.totalPages;
                    this.lastTotalCount = val.page.totalElements;
                })
            )
            .toPromise()
    }

    /**
     * The client certificates of a v3 frontdoor. Its own method rather than another value of the
     * `isV2` flag above: that flag is a boolean at every existing call site, and a third version
     * does not fit in it.
     *
     * The `/v3` segment is the gateway's, not the service's. The gateway strips `frontdoor/v3` and
     * the frontdoor service maps every child domain at `/{frontdoorId}/<plural>`.
     *
     * A listing omits each certificate's PEM. Reading one costs a Parameter Store call per row, so
     * the service leaves it out here — read the certificate on its own to get it.
     */
    public findV3ClientCertificates(frontdoorId: string, size = 2000): Observable<ClientCertificatePageV3> {
        const params = new HttpParams().set('sort', 'createdAt,DESC').set('size', String(size));
        return this.http
            .get(`${this.apiUrl}/v3/${frontdoorId}/client-certificates`, { headers: this.halHeaders(), params })
            .pipe(map((response: any) => this.readV3Page(response)));
    }

    /** Write a client certificate into a v3 frontdoor. */
    public createV3(frontdoorId: string, create: CreateClientCertificateV3): Observable<any> {
        return this.http.post(`${this.apiUrl}/v3/${frontdoorId}/client-certificates`, create, {
            headers: this.halHeaders(),
        });
    }

    /** One client certificate of a v3 frontdoor, for the pages that renew it or issue a renewal link for it. */
    public findV3Certificate(frontdoorId: string, id: string): Observable<ClientCertificate> {
        return this.http.get<ClientCertificate>(
            `${this.apiUrl}/v3/${frontdoorId}/client-certificates/${id}`,
            { headers: this.halHeaders() }
        );
    }

    /**
     * Replace the body of a certificate already on file, keeping its row.
     *
     * The id, the ARN and the name all survive, so no share pinned to this certificate needs to be
     * touched. That is the whole point of renewing in place rather than issuing a second certificate
     * and repointing the shares by hand. A running share reads the certificate when it starts, so it
     * accepts the new body after its next restart.
     */
    public renewV3(frontdoorId: string, id: string, update: RenewClientCertificateV3): Observable<any> {
        return this.http.patch(`${this.apiUrl}/v3/${frontdoorId}/client-certificates/${id}`, update, {
            headers: this.halHeaders(),
        });
    }

    /** Remove one certificate from a v3 frontdoor. */
    public deleteV3(frontdoorId: string, id: string): Observable<any> {
        return this.http.delete(`${this.apiUrl}/v3/${frontdoorId}/client-certificates/${id}`, {
            headers: this.halHeaders(),
        });
    }

    public delete(frontdoorId: string, id: string, isV2: boolean) {
        const headers = this.setHeaders();
        const version = isV2 ? '/v2' : '';
        const fullpath = `${this.apiUrl}${version}/${frontdoorId}/client-certificates/${id}`;
            return this.http
                .delete(fullpath, {headers: headers})
    }


    public getClientCertificates(frontdoorId: string, isV2: boolean) {
        const version = isV2 ? '/v2' : '';
        const fullpath = `${this.apiUrl}${version}/${frontdoorId}/client-certificates`
        return this.http
            .get(fullpath, {
    headers: { Accept: 'application/hal+json' }})
    }

    public getClientCertificateById(frontdoorId: string, id: string, isV2: boolean) {
        const version = isV2 ? '/v2' : '';
        const fullpath = `${this.apiUrl}${version}/${frontdoorId}/client-certificates/${id}`
        return this.http
            .get(fullpath, {
    headers: { Accept: 'application/hal+json' }})
    }

    public create(model, id: string, isV2: boolean) {
        const version = isV2 ? '/v2' : '';
        const fullpath = `${this.apiUrl}${version}/${id}/client-certificates`;
            return this.http
                .post(fullpath, model)
    }

    public update(model, frontdoorId: string, id, isV2: boolean) {
        const version = isV2 ? '/v2' : '';
        const fullpath = `${this.apiUrl}${version}/${frontdoorId}/client-certificates/${id}`;
            return this.http
                .patch(fullpath, model)
    }


    public getModel(): ClientCertificate | null {
            return this.model;
        }
        
    public setModel(data: ClientCertificate) {
            this.model = data;
        }


    /**
     * @param path
     * @param data
     */
    public post(path: string, data) {
        const fullpath = `${this.apiUrl}${path}`;
        const body = data;

        return this.http.post(fullpath, JSON.stringify(body), { headers: this.setHeaders() });
    }

    /**
     * @param path
     * @param data
     */
    public put(path: string, data) {
        const fullpath = `${this.apiUrl}${path}`;
        const body = data;

        return this.http.put(fullpath, JSON.stringify(body), { headers: this.setHeaders() });
    }

    /**
     * Generic GETter
     */

    public get(path: string) {
        const fullpath = `${this.apiUrl}${path}`;

        return this.http.get(fullpath, { headers: this.setHeaders() });
    }

    private halHeaders(): HttpHeaders {
        return new HttpHeaders().set('Accept', 'application/hal+json');
    }

    /** Accepts the HAL collection, a bare array, or a Spring page, so the shape can settle later. */
    private readV3Page(response: any): ClientCertificatePageV3 {
        if (Array.isArray(response)) {
            return { certificates: response, total: response.length };
        }
        const embedded = response?._embedded;
        const collection = embedded ? embedded.clientCertificates ?? Object.values(embedded)[0] : undefined;
        const certificates = Array.isArray(collection)
            ? collection
            : Array.isArray(response?.content)
              ? response.content
              : [];
        if (!Array.isArray(collection) && !Array.isArray(response?.content)) {
            this.logger.error('findV3ClientCertificates: unrecognised response shape', response);
        }
        const total = Number.isFinite(response?.page?.totalElements) ? response.page.totalElements : null;
        return { certificates, total };
    }

    /**
     * Return headers
     */
    private setHeaders(): HttpHeaders {
        const headers = new HttpHeaders();

                  return headers.append('Content-Type', 'application/json');
    }

        /**
     * Generic error handler for bad requests
     */
        protected handleError(scope: any, error: HttpErrorResponse) {
            if (error.error instanceof ErrorEvent) {
                // A client-side or network error occurred. Handle it accordingly.
                scope.logger.error('Feature Service error occurred:', error.error);
            } else {
                // The backend returned an unsuccessful response code.
                // The response body may contain clues as to what went wrong,
                scope.logger.error(`Feature Backend returned code ${error.status}`, error.error);
            }
            scope.lastErrorSource.next(error);
            return observableThrowError(error);
        }
}
